import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const API_URL = `${API_BASE}/customers`;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);

      const response = await fetch(API_URL);
      const data = await response.json();

      console.log("Customers loaded:", data);

      setCustomers(Array.isArray(data) ? data : (data?.data || []));
    } catch (error) {
      console.error("Failed to load customers:", error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();

    return customers.filter((c) => {
      const id =
        c.customerRef ||
        c.customerId ||
        c._id?.slice(-6).toUpperCase() ||
        "";

      const name =
        c.name ||
        c.contactName ||
        c.customerName ||
        "";

      const company =
        c.companyName ||
        c.company ||
        c.businessName ||
        "";

      const email = c.email || "";

      const type =
        c.customerType ||
        c.type ||
        "general";

      const matchesQuery =
        q === "" ||
        name.toLowerCase().includes(q) ||
        company.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        id.toLowerCase().includes(q);

      const matchesType =
        typeFilter === "All" ||
        type.toLowerCase() === typeFilter.toLowerCase();

      return matchesQuery && matchesType;
    });
  }, [customers, query, typeFilter]);

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Customers</h1>

        <p className="text-sm text-gray-500">
          Find, manage, and edit customer accounts.
        </p>
      </div>

      <div className="border border-gray-300 rounded-lg bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
          <input
            type="text"
            placeholder="Search name, company, email, or customer ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          />

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm bg-white"
          >
            <option>All</option>
            <option value="livestock">Livestock</option>
            <option value="freight">Freight</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-300">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold text-gray-700">
                  ID
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Customer
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Contact
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Email
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Phone
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Type
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Status
                </th>

                <th className="px-4 py-3 font-semibold text-gray-700">
                  Portal
                </th>

                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    Loading customers...
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((c) => {
                  const id =
                    c.customerRef ||
                    c.customerId ||
                    c._id?.slice(-6).toUpperCase();

                  const customerName =
                    c.companyName ||
                    c.company ||
                    c.businessName ||
                    "Unnamed Customer";

                  const contactName =
                    c.contactName ||
                    c.name ||
                    "-";

                  const type =
                    c.customerType ||
                    c.type ||
                    "general";

                  const status =
                    c.status || "active";

                  return (
                    <tr
                      key={c._id || id}
                      className="border-t border-gray-200"
                    >
                      <td className="px-4 py-3">{id}</td>

                      <td className="px-4 py-3 font-medium">
                        {customerName}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-700">
                        {contactName}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-600">
                        {c.email || "-"}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-600">
                        {c.phone || "-"}
                      </td>

                      <td className="px-4 py-3 capitalize">
                        {type}
                      </td>

                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs rounded bg-emerald-100 text-emerald-700 capitalize">
                          {status}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        {c.portalLogin?.loginEnabled ? (
                          <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                            Active
                          </span>
                        ) : c.portalLogin?.setupToken ? (
                          <span className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700">
                            Pending
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-500">
                            Not Set Up
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/dashboard/customers/${c._id}`}
                            className="border border-gray-300 px-3 py-1 rounded text-xs hover:bg-gray-50 inline-block"
                          >
                            Open
                          </Link>
<Link
  to={`/dashboard/customers/${c._id}/edit`}
  className="bg-black text-white px-3 py-1 rounded text-xs hover:bg-gray-800"
>
  Edit
</Link>

                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    No customers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <Link
          to="/dashboard/customers/new"
          className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
        >
          + New Customer
        </Link>
      </div>
    </div>
  );
};

export default Customers;